import type { SavedReport } from '@clockwise/clients/report-definitions';
import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Clock, Grid2x2, Table2 } from 'lucide-react';
import { z } from 'zod';
import { ShareBadge } from '@/components/custom/share-badge';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import {
  isLibraryView,
  type LibraryView,
  MAX_SEARCH,
  usesReportsLibrary,
} from '@/components/reports/library/library-model';
import { ReportsLibrary } from '@/components/reports/library/reports-library';
import { Skeleton } from '@/components/ui/skeleton';
import { reportDefinitionsApi } from '@/lib/api';
import { useMe } from '@/lib/me';
import { queryKeys } from '@/lib/query-keys';

/**
 * The view and the search live in the URL, so a label or a search is a link
 * somebody can send. An unrecognised view falls back to every report rather
 * than failing the route.
 */
const reportsSearch = z.object({
  view: z
    .string()
    .optional()
    .transform((v) => (v && isLibraryView(v) ? v : undefined)),
  // Trimmed rather than refused: a long pasted link should open the page with
  // its search cut short, not the route's error screen.
  q: z
    .string()
    .optional()
    .transform((v) => v?.slice(0, MAX_SEARCH)),
});

export const Route = createFileRoute('/_authenticated/reports/')({
  validateSearch: reportsSearch,
  component: ReportsIndexPage,
});

// Catalog of reports that ship WITH THE PRODUCT. New ones add an entry here +
// a sibling route under /reports. Saved reports are data and are listed
// separately below — the two are different kinds of thing: these cannot be
// edited or deleted (CR-2.10), those belong to somebody.
//
// `permission` is the EXTRA id a card needs beyond the layout's `report.view`.
// Omitted means the section gate is the whole gate.
const BUILT_IN = [
  {
    to: '/reports/timesheets',
    title: 'Timesheet report',
    description:
      'Timesheet entries across employees and customers for a date range and status, exportable to Excel.',
    icon: Clock,
    preview: false,
    permission: undefined,
  },
  {
    to: '/reports/skill-matrix',
    title: 'Skill matrix',
    description:
      'Who knows what, at what proficiency, beside this month’s available hours and location.',
    icon: Grid2x2,
    preview: false,
    permission: 'skills.view',
  },
  {
    to: '/reports/new',
    title: 'Build a report',
    description:
      'Choose a subject area — utilization, timesheets or budget vs actuals — group it across rows and columns, and show the result as a table or a chart.',
    icon: Table2,
    // Flagged rather than hidden: the vocabulary and rules are real and worth
    // reviewing, but the builder is still gaining pieces.
    preview: true,
    permission: 'report.edit',
  },
] as const;

function ReportsIndexPage() {
  const { permissions, loading } = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = { scope: 'all', sortField: 'name', sortDirection: 'asc', limit: 200 } as const;
  const saved = useQuery({
    queryKey: queryKeys.reportDefinitions.list(query),
    queryFn: () => reportDefinitionsApi.list(query),
  });

  // Undecided until both answers are in: without `report.edit` the list's
  // shares decide, and before /me loads nobody holds anything. Drawing either
  // page early would flash the wrong one and then swap it out.
  if (loading || (!permissions.has('report.edit') && saved.isPending)) {
    return (
      <PageShell>
        <PageHeader title="Reports" />
        <div className="space-y-2" role="status" aria-busy="true" aria-label="Loading reports">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </PageShell>
    );
  }

  if (usesReportsLibrary(permissions, saved.data?.data)) {
    return (
      <ReportsLibrary
        saved={saved}
        view={(search.view as LibraryView | undefined) ?? 'all'}
        q={search.q ?? ''}
        onSearch={(next) =>
          navigate({
            search: (s) => ({
              ...s,
              ...next,
              // Defaults stay out of the URL, so the bare page is `/reports`.
              view: (next.view ?? s.view) === 'all' ? undefined : (next.view ?? s.view),
              q: (next.q ?? s.q) || undefined,
            }),
            // Typing in the search box is not navigation — Back should leave
            // the page, not step back through every keystroke.
            replace: next.q !== undefined,
          })
        }
      />
    );
  }
  return <OriginalReportsPage />;
}

/** The page as it was before labels — still what most people get. */
function OriginalReportsPage() {
  // A card nobody can open is not an advertisement, it is a dead end. The page
  // itself and both endpoints refuse the same id; hiding the card is so the
  // refusal is never reached by clicking something we offered.
  const { permissions } = useMe();
  const visible = BUILT_IN.filter((r) => !r.permission || permissions.has(r.permission));

  // ONE request for both sections. `scope: 'all'` partitions on the server —
  // `mine` is ownership and `shared` excludes it — so splitting the list here
  // cannot double-count a report you own and also shared with everyone.
  const query = { scope: 'all', sortField: 'name', sortDirection: 'asc', limit: 200 } as const;
  const saved = useQuery({
    queryKey: queryKeys.reportDefinitions.list(query),
    queryFn: () => reportDefinitionsApi.list(query),
  });

  const mine = saved.data?.data.filter((r) => r.owned) ?? [];
  const shared = saved.data?.data.filter((r) => !r.owned) ?? [];
  // 200 is the server's ceiling, so a tenant past it loses the remainder.
  // Silent truncation is the failure mode to avoid: someone whose report is
  // missing has no way to tell "cut off" from "deleted", and would go looking
  // for it in the wrong place. `meta.total` is already in the response.
  const total = saved.data?.meta.total ?? 0;
  const truncated = total > (saved.data?.data.length ?? 0);

  return (
    <PageShell>
      <PageHeader title="Reports" subtitle="Run and export operational reports." />

      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((r) => (
          <li key={r.to}>
            <Link
              to={r.to}
              // `surface-card` — the app's one card fill (see the utility's
              // own comment in globals.css): a bordered box with no
              // background was invisible against the cream page instead of
              // reading as a card.
              className="surface-card flex h-full flex-col gap-2 rounded-lg border p-4 transition-colors hover:border-primary hover:bg-accent/40"
            >
              <span className="flex items-center gap-2 font-medium">
                <r.icon className="text-muted-foreground" />
                {r.title}
                {r.preview ? (
                  <span className="rounded-full border px-2 py-0.5 font-normal text-[10px] text-muted-foreground uppercase tracking-wide">
                    Preview
                  </span>
                ) : null}
              </span>
              <span className="text-sm text-muted-foreground">{r.description}</span>
            </Link>
          </li>
        ))}
      </ul>

      {/* G15: a list that failed to load must not read as a list with nothing
          in it — "you have no saved reports" is a claim, and this is the one
          case where it would be a false one. */}
      {saved.isError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="min-w-0">
            <span className="font-medium text-destructive">Couldn’t load your saved reports.</span>{' '}
            {saved.error instanceof ApiError ? saved.error.message : 'Please try again.'}
          </p>
        </div>
      )}

      {saved.isPending && (
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {/* A HEADING OVER NOTHING IS NOISE, and a permanent empty section teaches
          people to skip the heading — which is the heading that has to catch
          the eye on the day something does appear under it. So each section is
          drawn only when it has something to say.

          "My reports" survives one empty case: somebody who CAN build one, for
          whom the blank state is an invitation rather than a dead end. It also
          stays for somebody who owns reports but has since lost `report.edit`,
          so a revoked grant does not strand them from their own work. */}
      {saved.isSuccess && (
        <>
          {(mine.length > 0 || permissions.has('report.edit')) && (
            <SavedSection
              title="My reports"
              empty="Nothing saved yet — build a report and give it a name."
              reports={mine}
            />
          )}
          {shared.length > 0 && <SavedSection title="Shared with me" reports={shared} />}
          {truncated && (
            <p className="text-muted-foreground text-sm">
              Showing {saved.data.data.length} of {total} reports. Narrow the list by searching, or
              ask for paging if you need the rest.
            </p>
          )}
        </>
      )}
    </PageShell>
  );
}

/**
 * One titled group of saved reports.
 *
 * `empty` is optional because only the caller that can be legitimately empty
 * passes one — a section with no reports and nothing to say about it is not
 * rendered at all.
 */
function SavedSection({
  title,
  empty,
  reports,
}: {
  title: string;
  empty?: string;
  reports: SavedReport[];
}) {
  return (
    <section className="space-y-2">
      <h2 className="font-medium text-sm">{title}</h2>
      {reports.length === 0 ? (
        <p className="text-muted-foreground text-sm">{empty}</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {reports.map((r) => (
            <li key={r.id}>
              <Link
                to="/reports/custom/$reportId"
                params={{ reportId: r.id }}
                // `surface-card` — same card fill as the report-type tiles
                // above; this list was missing it too.
                className="surface-card flex h-full flex-col gap-2 rounded-lg border p-4 transition-colors hover:border-primary hover:bg-accent/40"
              >
                <span className="flex items-center gap-2 font-medium">
                  <Table2 className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{r.name}</span>
                  <ShareBadge owned={r.owned} shares={r.shares} />
                </span>
                {/* The description is what a recipient reads to decide whether
                    to run it (CR-3.7), so its absence is worth naming rather
                    than leaving the card a bare title. */}
                <span className="text-sm text-muted-foreground">
                  {r.description ?? 'No description.'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
